import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  useHistoricalResultsJWTQuery,
  useLeaderboardJWTQuery,
  useTrackPerformanceQuery,
} from "@/api/vercel";
import { format, subDays } from "date-fns";
import { Helmet } from "react-helmet-async";
import { cn } from "@/lib/utils";
import { isSessionValid } from "@/utils/auth";
import { logger } from "@/utils/logger";
import { CalendarIcon, ChevronDown, ChevronUp } from "lucide-react";
import { AlertTriangle, LockIcon, Search } from "lucide-react";
import { AnalysisDashboard } from "@/components/AnalysisDashboard";
import DashboardErrorBoundary from "@/components/DashboardErrorBoundary";
import AuthModal from "@/components/auth-modal";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RoiCell } from "@/components/ui/roi-cell";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Footer from "@/pages/landing/footer";

const states = {
  qld: "Queensland",
  nsw: "New South Wales",
  vic: "Victoria",
  sa: "South Australia",
  wa: "Western Australia",
  tas: "Tasmania",
  nt: "Northern Territory",
  act: "Australian Capital Territory",
};

// Animated sorting arrow component
const SortArrow = ({ isActive, direction }) => {
  if (!isActive) return null;

  const Icon = direction === "asc" ? ChevronUp : ChevronDown;

  return (
    <Icon
      className={`inline-block w-4 h-4 ml-1 transition-transform duration-200 ${
        isActive ? "text-[#F30099]" : "text-gray-400"
      }`}
      style={{
        transform: isActive ? "scale(1)" : "scale(0.8)",
      }}
    />
  );
};

export default function AnalysisOverview() {
  const [tracks, setTracks] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "totalROI",
    direction: "desc",
  });
  const [trackNames, setTrackNames] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [roiDisplayMode, setRoiDisplayMode] = useState("roi"); // 'roi', 'winRate', or 'both'
  const [dateFilter, setDateFilter] = useState("all"); // 'all', 'today', 'yesterday', 'custom'
  const [customDate, setCustomDate] = useState("");
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined,
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const isAuthenticated = isSessionValid();

  // Filter states
  const [filters, setFilters] = useState({
    trackNames: [],
    states: "",
    minWins: "",
    minRaces: "",
    minROI: "",
  });

  const [isTrackFilterOpen, setIsTrackFilterOpen] = useState(false);
  const [trackSearchTerm, setTrackSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const trackFilterRef = useRef(null);
  const [showFilters, setShowFilters] = useState(true);

  // Cache for processed track lists
  const [cachedTracks, setCachedTracks] = useState({
    today: null,
    yesterday: null,
    custom: null,
    lastUpdated: {
      today: null,
      yesterday: null,
      custom: null,
    },
  });

  // Date calculations
  const today = format(new Date(), "dd-MM-yyyy");
  const yesterday = format(subDays(new Date(), 1), "dd-MM-yyyy");

  // Human-readable label for the active date filter, shown to the right of
  // the Custom button (today's date, yesterday's date, or the picked range).
  const getSelectedDateLabel = () => {
    switch (dateFilter) {
      case "today":
        return format(new Date(), "d MMM yyyy");
      case "yesterday":
        return format(subDays(new Date(), 1), "d MMM yyyy");
      case "custom":
        if (dateRange.from) {
          return dateRange.to
            ? `${format(dateRange.from, "d MMM")} – ${format(dateRange.to, "d MMM yyyy")}`
            : format(dateRange.from, "d MMM yyyy");
        }
        return "Select a date range";
      default:
        return "";
    }
  };

  // Get historical results for today and yesterday with proper caching using JWT
  // Only fetch if user is authenticated
  const { data: todayResults } = useHistoricalResultsJWTQuery({
    date: today,
    fetchAll: false,
    staleTime: 30 * 60 * 1000, // 30 minutes - data stays fresh
    gcTime: 24 * 60 * 60 * 1000, // 24 hours - keep in cache for full day
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isAuthenticated, // Only fetch if user is authenticated
  });

  const { data: yesterdayResults } = useHistoricalResultsJWTQuery({
    date: yesterday,
    fetchAll: false,
    staleTime: 60 * 60 * 1000, // 1 hour - yesterday data is more stable
    gcTime: 48 * 60 * 60 * 1000, // 48 hours - keep yesterday data longer
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isAuthenticated, // Only fetch if user is authenticated
  });

  // Get custom date results with caching using JWT
  const { data: customDateResults } = useHistoricalResultsJWTQuery({
    date: customDate,
    fetchAll: false,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 48 * 60 * 60 * 1000, // 48 hours
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isAuthenticated && customDate !== "" && dateFilter === "custom", // Only fetch if user is authenticated and has set a custom date
  });

  // Get leaderboard data for today (for tracks with tips) with caching using JWT
  // Only fetch if user is authenticated
  const { data: todayLeaderboard } = useLeaderboardJWTQuery({
    date: today,
    collection: "predictions",
    staleTime: 15 * 60 * 1000, // 15 minutes - tips data updates more frequently
    gcTime: 24 * 60 * 60 * 1000, // 24 hours
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isAuthenticated, // Only fetch if user is authenticated
  });

  // Process and cache tracks for specific dates - optimized
  useEffect(() => {
    // Process today's tracks
    if (todayResults?.data || todayLeaderboard?.data) {
      const currentDate = today;

      // Check if we need to update today's cache
      if (cachedTracks.lastUpdated.today !== currentDate) {
        let todayTracks = [];

        // Check historical results for today
        if (todayResults?.data) {
          const historicalTracks = Object.keys(todayResults.data)
            .map((key) => {
              const parts = key.split("-");
              return parts.slice(0, parts.length - 4).join(" ");
            })
            .filter((track, index, self) => self.indexOf(track) === index);
          todayTracks = [...todayTracks, ...historicalTracks];
        }

        // Check leaderboard data for today
        if (todayLeaderboard?.data) {
          const leaderboardTracks = Object.keys(todayLeaderboard.data)
            .map((key) => {
              const parts = key.split("-");
              return parts.slice(0, parts.length - 4).join(" ");
            })
            .filter((track, index, self) => self.indexOf(track) === index);
          todayTracks = [...todayTracks, ...leaderboardTracks];
        }

        // Remove duplicates
        const uniqueTodayTracks = todayTracks.filter(
          (track, index, self) => self.indexOf(track) === index,
        );

        setCachedTracks((prev) => ({
          ...prev,
          today: uniqueTodayTracks,
          lastUpdated: {
            ...prev.lastUpdated,
            today: currentDate,
          },
        }));
      }
    }
  }, [todayResults, todayLeaderboard, today, cachedTracks.lastUpdated.today]);

  useEffect(() => {
    // Process yesterday's tracks - optimized
    if (yesterdayResults?.data) {
      const currentDate = yesterday;

      // Check if we need to update yesterday's cache
      if (cachedTracks.lastUpdated.yesterday !== currentDate) {
        const yesterdayTracks = Object.keys(yesterdayResults.data)
          .map((key) => {
            const parts = key.split("-");
            return parts.slice(0, parts.length - 4).join(" ");
          })
          .filter((track, index, self) => self.indexOf(track) === index);

        setCachedTracks((prev) => ({
          ...prev,
          yesterday: yesterdayTracks,
          lastUpdated: {
            ...prev.lastUpdated,
            yesterday: currentDate,
          },
        }));
      }
    }
  }, [yesterdayResults, yesterday, cachedTracks.lastUpdated.yesterday]);

  useEffect(() => {
    // Process custom date tracks - optimized
    if (customDateResults?.data && customDate) {
      // Check if we need to update custom date cache
      if (cachedTracks.lastUpdated.custom !== customDate) {
        const customTracks = Object.keys(customDateResults.data)
          .map((key) => {
            const parts = key.split("-");
            return parts.slice(0, parts.length - 4).join(" ");
          })
          .filter((track, index, self) => self.indexOf(track) === index);

        setCachedTracks((prev) => ({
          ...prev,
          custom: customTracks,
          lastUpdated: {
            ...prev.lastUpdated,
            custom: customDate,
          },
        }));
      }
    }
  }, [customDateResults, customDate, cachedTracks.lastUpdated.custom]);

  // Get tracks for specific date from cache - optimized
  const getTracksForDate = useMemo(() => {
    if (dateFilter === "today") {
      return cachedTracks.today || [];
    } else if (dateFilter === "yesterday") {
      return cachedTracks.yesterday || [];
    } else if (dateFilter === "custom") {
      return cachedTracks.custom || [];
    }
    return [];
  }, [
    dateFilter,
    cachedTracks.today,
    cachedTracks.yesterday,
    cachedTracks.custom,
  ]);

  // Debounced filters for numeric inputs to prevent excessive filtering
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300); // 300ms debounce for filter changes

    return () => clearTimeout(timer);
  }, [filters]);

  // Filtered and sorted tracks - optimized without expensive logging
  const filteredTracks = useMemo(() => {
    let tracksToFilter = tracks;

    // Apply date filter first - optimized
    if (dateFilter !== "all" && getTracksForDate.length > 0) {
      const dateTracksLower = getTracksForDate.map((track) =>
        track.toLowerCase(),
      );
      tracksToFilter = tracks.filter((track) =>
        dateTracksLower.includes(track.name.toLowerCase()),
      );
    }

    // Use debounced filters for better performance
    const filtersToUse = debouncedFilters;

    const filtered = tracksToFilter.filter((track) => {
      // Track name filter - optimized
      if (filtersToUse.trackNames.length > 0) {
        const trackNameLower = track.name.toLowerCase();
        const hasMatch = filtersToUse.trackNames.some(
          (name) => trackNameLower === name.toLowerCase(),
        );
        if (!hasMatch) return false;
      }

      // State filter - optimized
      if (filtersToUse.states) {
        const stateMatch = track.stateName
          .toLowerCase()
          .includes(filtersToUse.states.toLowerCase());
        if (!stateMatch) return false;
      }

      // Numeric filters - optimized with early returns
      if (filtersToUse.minWins) {
        const minWins = parseInt(filtersToUse.minWins);
        if (!isNaN(minWins) && track.wins < minWins) return false;
      }

      if (filtersToUse.minRaces) {
        const minRaces = parseInt(filtersToUse.minRaces);
        if (!isNaN(minRaces) && track.totalRaces < minRaces) return false;
      }

      if (filtersToUse.minROI) {
        const minROI = parseFloat(filtersToUse.minROI);
        if (!isNaN(minROI) && track.roi < minROI) return false;
      }

      return true;
    });

    // Apply sorting - optimized
    if (sortConfig.key && filtered.length > 0) {
      filtered.sort((a, b) => {
        let aValue;
        let bValue;

        // Handle position-based sorting
        if (sortConfig.key.includes("_p")) {
          const [timeType, position] = sortConfig.key.split("_p");
          const positionKey = `p${position}`;
          const metricMode = roiDisplayMode === "winRate" ? "winRate" : "roi"; // default to ROI when showing both

          const getPositionMetric = (track) => {
            const positionData = track[timeType]?.positions?.[positionKey];
            if (!positionData) return 0;

            if (metricMode === "winRate") {
              const bets = positionData.bets ?? 0;
              const wins = positionData.wins ?? 0;
              if (!bets || bets <= 0) return 0;
              return (wins / bets) * 100;
            }

            const roiValue = positionData.roi;
            if (typeof roiValue === "number") return roiValue;
            const parsedRoi = parseFloat(roiValue);
            return Number.isFinite(parsedRoi) ? parsedRoi : 0;
          };

          aValue = getPositionMetric(a);
          bValue = getPositionMetric(b);
        } else {
          // Handle regular field sorting
          aValue = a[sortConfig.key] ?? 0;
          bValue = b[sortConfig.key] ?? 0;
        }

        // Ensure string fields (like track name) sort via localeCompare
        if (typeof aValue === "string" || typeof bValue === "string") {
          const aString = (aValue ?? "").toString().toLowerCase();
          const bString = (bValue ?? "").toString().toLowerCase();
          const comparison = aString.localeCompare(bString);
          return sortConfig.direction === "asc" ? comparison : -comparison;
        }

        const aNumber = Number.isFinite(aValue) ? aValue : Number(aValue) || 0;
        const bNumber = Number.isFinite(bValue) ? bValue : Number(bValue) || 0;

        if (sortConfig.direction === "asc") {
          return aNumber - bNumber;
        }
        return bNumber - aNumber;
      });
    }

    return filtered;
  }, [
    tracks,
    debouncedFilters,
    dateFilter,
    getTracksForDate,
    sortConfig,
    roiDisplayMode,
  ]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Optimized search input handler with immediate state update
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setTrackSearchTerm(value);

    // For very short searches, update immediately to feel more responsive
    if (value.length <= 2) {
      setDebouncedSearchTerm(value);
    }
  }, []);

  // Debounce search term for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(trackSearchTerm);
    }, 100); // Reduced to 100ms for more responsive typing

    return () => clearTimeout(timer);
  }, [trackSearchTerm]);

  // Close track filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        trackFilterRef.current &&
        !trackFilterRef.current.contains(event.target)
      ) {
        setIsTrackFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Load track names data
  useEffect(() => {
    async function loadTrackNames() {
      try {
        const response = await fetch(
          "/data/horseracing/tracks/track-names.json",
        );
        const data = await response.json();
        setTrackNames(data);
      } catch (err) {
        logger.error("Error loading track names:", err);
      }
    }
    loadTrackNames();
  }, []);

  // Use the track performance query hook
  const {
    data: trackPerformanceData,
    isLoading,
    error,
  } = useTrackPerformanceQuery({
    // Must exceed the total number of tracks with performance data (~210) so the
    // API doesn't truncate the tail of the alphabet (e.g. Wyong) from the dropdown.
    limit: 1000,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: true,
  });

  // Memoized track processing for better performance
  const processedTracks = useMemo(() => {
    if (!trackPerformanceData?.data || Object.keys(trackNames).length === 0) {
      return [];
    }

    const trackStats = new Map();

    trackPerformanceData.data.forEach((track) => {
      // Skip if no models data
      if (!track.models?.HayiAuto) return;

      const trackData = track.models.HayiAuto;
      const trackName = track.id;

      // Get track info including state - with better matching logic
      let trackInfo = trackNames[trackName];

      if (!trackInfo) {
        // Try to find by alt name
        trackInfo = Object.values(trackNames).find((t) => t.alt === trackName);
      }

      if (!trackInfo) {
        // Try case-insensitive matching
        const trackNameLower = trackName.toLowerCase();
        trackInfo = Object.entries(trackNames).find(
          ([key, value]) =>
            key.toLowerCase() === trackNameLower ||
            value.alt?.toLowerCase() === trackNameLower,
        )?.[1];
      }

      if (!trackInfo) {
        // Try partial matching for tracks with spaces and hyphens
        const trackNameNormalized = trackName.replace(/\s+/g, " ").trim();
        trackInfo = Object.entries(trackNames).find(
          ([key, value]) =>
            key.replace(/\s+/g, " ").trim().toLowerCase() ===
              trackNameNormalized.toLowerCase() ||
            value.alt?.replace(/\s+/g, " ").trim().toLowerCase() ===
              trackNameNormalized.toLowerCase(),
        )?.[1];
      }

      if (!trackInfo) {
        // Try matching with hyphen-to-space conversion
        const trackNameWithSpaces = trackName
          .replace(/-/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        const trackNameWithHyphens = trackName.replace(/\s+/g, "-").trim();

        trackInfo = Object.entries(trackNames).find(([key, value]) => {
          const keyWithSpaces = key
            .replace(/-/g, " ")
            .replace(/\s+/g, " ")
            .trim();
          const keyWithHyphens = key.replace(/\s+/g, "-").trim();
          const altWithSpaces = value.alt
            ?.replace(/-/g, " ")
            .replace(/\s+/g, " ")
            .trim();
          const altWithHyphens = value.alt?.replace(/\s+/g, "-").trim();

          return (
            keyWithSpaces.toLowerCase() === trackNameWithSpaces.toLowerCase() ||
            keyWithHyphens.toLowerCase() ===
              trackNameWithHyphens.toLowerCase() ||
            altWithSpaces?.toLowerCase() ===
              trackNameWithSpaces.toLowerCase() ||
            altWithHyphens?.toLowerCase() === trackNameWithHyphens.toLowerCase()
          );
        })?.[1];
      }

      if (!trackInfo) {
        return;
      }

      // Calculate total stats from early and raceTime data
      const totalBets =
        trackData.early.totalBets + trackData.raceTime.totalBets;
      const totalWins =
        Object.values(trackData.early.positions).reduce(
          (sum, pos) => sum + pos.wins,
          0,
        ) +
        Object.values(trackData.raceTime.positions).reduce(
          (sum, pos) => sum + pos.wins,
          0,
        );
      const totalPlaces =
        Object.values(trackData.early.positions).reduce(
          (sum, pos) => sum + (pos.roi > 0 ? 1 : 0),
          0,
        ) +
        Object.values(trackData.raceTime.positions).reduce(
          (sum, pos) => sum + (pos.roi > 0 ? 1 : 0),
          0,
        );
      const avgROI = (trackData.early.roi + trackData.raceTime.roi) / 2;
      const totalReturns =
        trackData.early.totalReturns + trackData.raceTime.totalReturns;
      const netReturns = totalReturns - totalBets;

      // Calculate total ROI across all positions
      const totalROI =
        Object.values(trackData.early.positions).reduce(
          (sum, pos) => sum + (pos.roi || 0),
          0,
        ) +
        Object.values(trackData.raceTime.positions).reduce(
          (sum, pos) => sum + (pos.roi || 0),
          0,
        );

      // Calculate total win rate across all positions
      const totalWinsForWinRate =
        Object.values(trackData.early.positions).reduce(
          (sum, pos) => sum + (pos.wins || 0),
          0,
        ) +
        Object.values(trackData.raceTime.positions).reduce(
          (sum, pos) => sum + (pos.wins || 0),
          0,
        );
      const totalBetsForWinRate =
        Object.values(trackData.early.positions).reduce(
          (sum, pos) => sum + (pos.bets || 0),
          0,
        ) +
        Object.values(trackData.raceTime.positions).reduce(
          (sum, pos) => sum + (pos.bets || 0),
          0,
        );
      const totalWinRate =
        totalBetsForWinRate > 0
          ? (totalWinsForWinRate / totalBetsForWinRate) * 100
          : 0;

      // Calculate win rates for all positions to find min/max for relative coloring
      const allWinRates = [];
      Object.values(trackData.early.positions).forEach((pos) => {
        if (pos.bets > 0) {
          allWinRates.push((pos.wins / pos.bets) * 100);
        }
      });
      Object.values(trackData.raceTime.positions).forEach((pos) => {
        if (pos.bets > 0) {
          allWinRates.push((pos.wins / pos.bets) * 100);
        }
      });

      const minWinRate = allWinRates.length > 0 ? Math.min(...allWinRates) : 0;
      const maxWinRate = allWinRates.length > 0 ? Math.max(...allWinRates) : 0;

      trackStats.set(trackName, {
        name: trackName,
        state: trackInfo.State.toLowerCase(),
        stateName: states[trackInfo.State.toLowerCase()] || trackInfo.State,
        totalRaces: trackData.totalRaces,
        wins: totalWins,
        places: totalPlaces,
        roi: avgROI,
        totalROI: totalROI,
        totalWinRate: totalWinRate,
        totalBets: totalBets,
        totalReturns: totalReturns,
        netReturns: netReturns,
        minWinRate,
        maxWinRate,
        early: trackData.early,
        raceTime: trackData.raceTime,
      });
    });

    // Convert to array and sort by Total ROI
    return Array.from(trackStats.values()).sort(
      (a, b) => b.totalROI - a.totalROI,
    );
  }, [trackPerformanceData, trackNames]);

  // Update tracks state only when processedTracks changes
  useEffect(() => {
    setTracks(processedTracks);
  }, [processedTracks]);

  // Get unique track names for autocomplete - memoized more efficiently
  const uniqueTrackNames = useMemo(() => {
    if (tracks.length === 0) return [];

    const trackNamesSet = new Set();
    tracks.forEach((track) => trackNamesSet.add(track.name));
    return Array.from(trackNamesSet).sort();
  }, [tracks]);

  // Filter track names based on debounced search term - optimized
  const filteredTrackNames = useMemo(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.length === 0) {
      return uniqueTrackNames;
    }

    const searchLower = debouncedSearchTerm.toLowerCase();
    const filtered = [];

    for (let i = 0; i < uniqueTrackNames.length; i++) {
      const name = uniqueTrackNames[i];
      if (name.toLowerCase().includes(searchLower)) {
        filtered.push(name);
      }
    }

    return filtered;
  }, [uniqueTrackNames, debouncedSearchTerm]);

  // Calculate min/max total win rates across all tracks for relative coloring - optimized
  const totalWinRateStats = useMemo(() => {
    if (tracks.length === 0) return { min: 0, max: 0 };

    let min = Infinity;
    let max = -Infinity;

    for (let i = 0; i < tracks.length; i++) {
      const winRate = tracks[i].totalWinRate || 0;
      if (winRate < min) min = winRate;
      if (winRate > max) max = winRate;
    }

    return {
      min: min === Infinity ? 0 : min,
      max: max === -Infinity ? 0 : max,
    };
  }, [tracks]);

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    // Note: We don't need to update tracks here as sorting is handled by filteredTracks
    // This function is kept for compatibility but sorting will be handled in the useMemo
  };

  const sortDataByPosition = (timeType, position) => {
    const key = `${timeType}_p${position}`;
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    // Note: We don't need to update tracks here as sorting is handled by filteredTracks
    // This function is kept for compatibility but sorting will be handled in the useMemo
  };

  // Function to get color based on relative position in range
  const getRelativeColor = (value, min, max) => {
    if (max === min) return "bg-gray-100 text-gray-800"; // All same value

    const position = (value - min) / (max - min);

    if (position <= 0.25) return "bg-red-100 text-red-800";
    if (position <= 0.5) return "bg-orange-100 text-orange-800";
    if (position <= 0.75) return "bg-yellow-100 text-yellow-800";
    return "bg-green-100 text-green-800";
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card className="p-4 text-center text-red-500">
          Error loading track performance data: {error.message}
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
      <Helmet>
        <title>Racing Analysis & Performance | Lexi Tips</title>
        <meta
          name="description"
          content="Explore comprehensive horse racing analysis and performance statistics. View results by state, track, ROI, and more."
        />
        <meta
          name="keywords"
          content="horse racing analysis, racing statistics, track performance, racing tips, ROI analysis"
        />
      </Helmet>

      {/* Experimental Feature Banner */}
      <div className="w-screen relative left-1/2 -translate-x-1/2 bg-orange-100 border-y border-orange-200 mt-12 ">
        <div className="px-4 py-3">
          <div className="flex items-center justify-center gap-2 text-orange-800">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium text-sm">
              Experimental Feature - Use with caution
            </span>
            <AlertTriangle className="w-5 h-5" />
          </div>
        </div>
      </div>

      <div
        className={`${isFullWidth ? "research-mode-on w-full" : "research-mode-off container mx-auto"} py-8 ${isFullWidth ? "px-2" : "px-0 max-w-7xl"}`}
      >
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-4">
            Analysis
          </h1>
          <p className="text-gray-600 text-sm md:text-base">
            See how Lexi&apos;s tips have performed by track, date and race.
          </p>
        </div>

        <div className="space-y-8">
          {/* Performance Dashboard with Error Boundary */}
          <DashboardErrorBoundary>
            <AnalysisDashboard
              trackPerformanceData={trackPerformanceData}
              isLoading={isLoading}
              error={error}
            />
          </DashboardErrorBoundary>

          <Card
            id="all-tracks-performance"
            className="p-6 md:p-8 border border-[#F30099]/20 shadow-lg bg-white"
          >
            <div className="mb-2 flex justify-between items-start md:items-center">
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-2">
                  All Tracks Performance
                </h2>
              </div>
              {isAuthenticated && (
                <button
                  onClick={() => setIsFullWidth(!isFullWidth)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors focus:ring-2 focus:ring-[#F30099] focus:border-transparent"
                  title={
                    isFullWidth
                      ? "Collapse to normal width"
                      : "Expand to full width"
                  }
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {isFullWidth ? (
                      // Collapse icon (arrows pointing inward)
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                      />
                    ) : (
                      // Expand icon (arrows pointing outward)
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                      />
                    )}
                  </svg>
                  {isFullWidth ? "Collapse" : "Expand"}
                </button>
              )}
            </div>
            <p className="text-gray-600 mb-8">
              Filter and analyse performance data across different tracks and
              states.
            </p>

            {isAuthenticated ? (
              <>
                {/* Filters */}
                <div className="bg-gray-50 rounded-lg p-4 md:p-6 mb-8">
                  <div className="flex items-center justify-between mb-0">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Filters
                    </h3>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      {showFilters ? (
                        <>
                          <span>Hide</span>
                          <ChevronUp className="w-4 h-4" />
                        </>
                      ) : (
                        <>
                          <span>Show</span>
                          <ChevronDown className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                  {showFilters && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
                        {/* Track Name Filter */}
                        <div className="space-y-2">
                          <label
                            htmlFor="trackSelector"
                            className="text-sm pl-2 font-medium text-gray-700"
                          >
                            Track Name
                          </label>
                          <div className="relative" ref={trackFilterRef}>
                            {/* Single row display/button */}
                            <button
                              onClick={() =>
                                setIsTrackFilterOpen(!isTrackFilterOpen)
                              }
                              className="w-full p-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F30099] focus:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors text-left flex items-center justify-between"
                            >
                              <span className="text-sm text-gray-700">
                                {filters.trackNames.length === 0
                                  ? "Select tracks..."
                                  : `${filters.trackNames.length} track${filters.trackNames.length === 1 ? "" : "s"} selected`}
                              </span>
                              <div className="flex items-center gap-2">
                                {filters.trackNames.length > 0 && (
                                  <div className="bg-[#F30099] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                    {filters.trackNames.length}
                                  </div>
                                )}
                                <svg
                                  className={`w-4 h-4 text-gray-400 transition-transform ${isTrackFilterOpen ? "rotate-180" : ""}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </button>

                            {/* Dropdown with search */}
                            {isTrackFilterOpen && (
                              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-64 overflow-hidden">
                                {/* Search input */}
                                <div className="p-3 border-b border-gray-200">
                                  <input
                                    id="trackSelector"
                                    type="text"
                                    placeholder="Search tracks..."
                                    value={trackSearchTerm}
                                    onChange={handleSearchChange}
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#F30099] focus:border-transparent text-sm"
                                    autoFocus
                                  />
                                </div>

                                {/* Track list */}
                                <div className="max-h-48 overflow-y-auto">
                                  {filteredTrackNames.length === 0 ? (
                                    <div className="p-3 text-gray-500 text-sm text-center">
                                      No tracks found
                                    </div>
                                  ) : (
                                    filteredTrackNames.map((trackName) => (
                                      <label
                                        key={trackName}
                                        className="flex items-center p-2 hover:bg-gray-50 cursor-pointer"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={filters.trackNames.includes(
                                            trackName,
                                          )}
                                          onChange={(e) => {
                                            const newSelection = e.target
                                              .checked
                                              ? [
                                                  ...filters.trackNames,
                                                  trackName,
                                                ]
                                              : filters.trackNames.filter(
                                                  (name) => name !== trackName,
                                                );
                                            handleFilterChange(
                                              "trackNames",
                                              newSelection,
                                            );
                                          }}
                                          className="mr-2 text-[#F30099] focus:ring-[#F30099]"
                                        />
                                        <span className="text-sm">
                                          {trackName}
                                        </span>
                                      </label>
                                    ))
                                  )}
                                </div>

                                {/* Clear all button */}
                                {filters.trackNames.length > 0 && (
                                  <div className="p-2 border-t border-gray-200">
                                    <button
                                      onClick={() => {
                                        handleFilterChange("trackNames", []);
                                        setTrackSearchTerm("");
                                      }}
                                      className="w-full text-sm text-gray-600 hover:text-gray-800 py-1"
                                    >
                                      Clear all selections
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* State Filter */}
                        <div className="space-y-2">
                          <label
                            htmlFor="stateSelector"
                            className="text-sm pl-2 font-medium text-gray-700"
                          >
                            State
                          </label>
                          <div className="relative">
                            <select
                              id="stateSelector"
                              value={filters.states}
                              onChange={(e) =>
                                handleFilterChange("states", e.target.value)
                              }
                              className="w-full text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F30099] focus:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors"
                            >
                              <option value="">Select state...</option>
                              {Object.entries(states).map(([code, name]) => (
                                <option key={code} value={name}>
                                  {name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Min Races Filter */}
                        <div className="space-y-2">
                          <label
                            htmlFor="racesSelector"
                            className="text-sm pl-2 font-medium text-gray-700"
                          >
                            Min Races
                          </label>
                          <Input
                            id="racesSelector"
                            type="number"
                            placeholder="e.g. 50 (recommended)"
                            value={filters.minRaces}
                            onChange={(e) =>
                              handleFilterChange("minRaces", e.target.value)
                            }
                            className="w-full text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F30099] focus:border-transparent focus-visible:ring-2 focus-visible:ring-[#F30099] focus-visible:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors"
                            min="0"
                          />
                        </div>

                        {/* Min Wins Filter */}
                        <div className="space-y-2">
                          <label
                            htmlFor="winsSelector"
                            className="text-sm pl-2 font-medium text-gray-700"
                          >
                            Min Wins
                          </label>
                          <Input
                            id="winsSelector"
                            type="number"
                            placeholder="e.g. 25"
                            value={filters.minWins}
                            onChange={(e) =>
                              handleFilterChange("minWins", e.target.value)
                            }
                            className="w-full text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F30099] focus:border-transparent focus-visible:ring-2 focus-visible:ring-[#F30099] focus-visible:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors"
                            min="0"
                          />
                        </div>

                        {/* Min ROI Filter */}
                        <div className="space-y-2">
                          <label
                            htmlFor="roiSelector"
                            className="text-sm pl-2 font-medium text-gray-700"
                          >
                            Min ROI %
                          </label>
                          <Input
                            id="roiSelector"
                            type="number"
                            placeholder="e.g. 50"
                            value={filters.minROI}
                            onChange={(e) =>
                              handleFilterChange("minROI", e.target.value)
                            }
                            className="w-full text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F30099] focus:border-transparent focus-visible:ring-2 focus-visible:ring-[#F30099] focus-visible:border-transparent bg-white shadow-sm hover:border-gray-400 transition-colors"
                            step="0.1"
                          />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row justify-between gap-4 mt-4 pt-4 border-t border-gray-200">
                        {/* Date Filter Toggle */}
                        <div className="flex flex-col md:flex-row flex-start md:items-center gap-4">
                          <span className="text-sm font-medium text-gray-700">
                            Show Tracks:
                          </span>
                          <div className="flex items-center gap-3">
                            <div className="flex bg-gray-100 rounded-lg p-1 shadow-[inset_0_0_1px_0_rgba(0,0,0,0.45)]">
                              <button
                                onClick={() => {
                                  setDateFilter("all");
                                  setDateRange({
                                    from: undefined,
                                    to: undefined,
                                  });
                                  setCustomDate("");
                                }}
                                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                  dateFilter === "all"
                                    ? "bg-white text-[#F30099] shadow-sm"
                                    : "text-gray-600 hover:text-deepPink"
                                }`}
                              >
                                All
                              </button>
                              <button
                                onClick={() => {
                                  setDateFilter("today");
                                  setDateRange({
                                    from: undefined,
                                    to: undefined,
                                  });
                                  setCustomDate("");
                                }}
                                disabled={
                                  !cachedTracks.today ||
                                  cachedTracks.today.length === 0
                                }
                                title={
                                  !cachedTracks.today ||
                                  cachedTracks.today.length === 0
                                    ? "No data available - try again later"
                                    : ""
                                }
                                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                  !cachedTracks.today ||
                                  cachedTracks.today.length === 0
                                    ? "text-gray-400 cursor-not-allowed opacity-50"
                                    : dateFilter === "today"
                                      ? "bg-white text-[#F30099] shadow-sm"
                                      : "text-gray-600 hover:text-deepPink"
                                }`}
                              >
                                Todays
                              </button>
                              <button
                                onClick={() => {
                                  setDateFilter("yesterday");
                                  setDateRange({
                                    from: undefined,
                                    to: undefined,
                                  });
                                  setCustomDate("");
                                }}
                                disabled={
                                  !cachedTracks.yesterday ||
                                  cachedTracks.yesterday.length === 0
                                }
                                title={
                                  !cachedTracks.yesterday ||
                                  cachedTracks.yesterday.length === 0
                                    ? "No data available - try again later"
                                    : ""
                                }
                                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                  !cachedTracks.yesterday ||
                                  cachedTracks.yesterday.length === 0
                                    ? "text-gray-400 cursor-not-allowed opacity-50"
                                    : dateFilter === "yesterday"
                                      ? "bg-white text-[#F30099] shadow-sm"
                                      : "text-gray-600 hover:text-deepPink"
                                }`}
                              >
                                Yesterdays
                              </button>
                              <Popover
                                open={isDatePickerOpen}
                                onOpenChange={setIsDatePickerOpen}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    onClick={() => {
                                      // Clear any previous selection so a fresh
                                      // range can be picked each time Custom is
                                      // opened.
                                      setDateRange({
                                        from: undefined,
                                        to: undefined,
                                      });
                                      setCustomDate("");
                                      setDateFilter("custom");
                                    }}
                                    className={cn(
                                      "h-auto px-3 py-1 text-sm font-medium rounded-md border-0 transition-colors hover:bg-white hover:text-[#F30099]",
                                      dateFilter === "custom"
                                        ? "bg-white text-[#F30099] shadow-sm"
                                        : "bg-transparent text-gray-600",
                                    )}
                                  >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    Custom
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto p-0"
                                  align="start"
                                >
                                  <Calendar
                                    mode="range"
                                    selected={dateRange}
                                    onSelect={(range) => {
                                      setDateRange(
                                        range || {
                                          from: undefined,
                                          to: undefined,
                                        },
                                      );
                                      if (range?.from) {
                                        setCustomDate(
                                          format(range.from, "dd-MM-yyyy"),
                                        );
                                        setDateFilter("custom");
                                      } else {
                                        setCustomDate("");
                                        setDateFilter("all");
                                      }
                                      if (range?.to) {
                                        setIsDatePickerOpen(false);
                                      }
                                    }}
                                    maxDate={new Date()}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                            {getSelectedDateLabel() && (
                              <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
                                {getSelectedDateLabel()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Cell Display Toggle */}
                        <div className="flex flex-col md:flex-row flex-start md:items-center gap-4 ">
                          <span className="text-sm font-medium text-gray-700">
                            Show ROI / Win Rate:
                          </span>
                          <div className="flex bg-gray-100 rounded-lg p-1 shadow-[inset_0_0_1px_0_rgba(0,0,0,0.45)]">
                            <button
                              onClick={() => setRoiDisplayMode("both")}
                              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                roiDisplayMode === "both"
                                  ? "bg-white text-[#F30099] shadow-sm"
                                  : "text-gray-600 hover:text-deepPink"
                              }`}
                            >
                              Both
                            </button>
                            <button
                              onClick={() => setRoiDisplayMode("roi")}
                              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                roiDisplayMode === "roi"
                                  ? "bg-white text-[#F30099] shadow-sm"
                                  : "text-gray-600 hover:text-deepPink"
                              }`}
                            >
                              ROI
                            </button>
                            <button
                              onClick={() => setRoiDisplayMode("winRate")}
                              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                roiDisplayMode === "winRate"
                                  ? "bg-white text-[#F30099] shadow-sm"
                                  : "text-gray-700 hover:text-deepPink"
                              }`}
                            >
                              Win Rate
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Track Name Badges - Below the filters */}
                      {filters.trackNames.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {filters.trackNames.map((trackName) => (
                            <span
                              key={trackName}
                              className="inline-flex items-center gap-1 bg-[#F30099]/10 text-[#F30099] px-2 py-1 rounded-md text-sm font-medium"
                            >
                              {trackName}
                              <button
                                type="button"
                                onClick={() => {
                                  const newSelection =
                                    filters.trackNames.filter(
                                      (name) => name !== trackName,
                                    );
                                  handleFilterChange(
                                    "trackNames",
                                    newSelection,
                                  );
                                }}
                                className="ml-1 hover:bg-[#F30099]/20 rounded-full w-4 h-4 flex items-center justify-center"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="overflow-x-auto overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10 border-b shadow-sm">
                      <TableRow>
                        <TableHead className="text-left w-12 bg-white sticky top-0">
                          #
                        </TableHead>
                        <TableHead
                          className="text-left cursor-pointer bg-white sticky top-0 hover:bg-gray-50 transition-colors"
                          onClick={() => sortData("name")}
                        >
                          <div className="flex items-center">
                            Track
                            <SortArrow
                              isActive={sortConfig.key === "name"}
                              direction={sortConfig.direction}
                            />
                          </div>
                        </TableHead>
                        <TableHead
                          className="text-left cursor-pointer bg-white sticky top-0 hover:bg-gray-50 transition-colors"
                          onClick={() => sortData("totalRaces")}
                        >
                          <div className="flex items-center">
                            Races
                            <SortArrow
                              isActive={sortConfig.key === "totalRaces"}
                              direction={sortConfig.direction}
                            />
                          </div>
                        </TableHead>
                        {[1, 2, 3, 4].map((position) => (
                          <TableHead
                            key={`early-${position}`}
                            className="text-center cursor-pointer bg-white sticky top-0 hover:bg-gray-50 transition-colors"
                            onClick={() =>
                              sortDataByPosition("early", position)
                            }
                          >
                            <div className="flex items-center justify-center">
                              T{position}
                              <SortArrow
                                isActive={
                                  sortConfig.key === `early_p${position}`
                                }
                                direction={sortConfig.direction}
                              />
                            </div>
                          </TableHead>
                        ))}
                        {[1, 2, 3, 4].map((position) => (
                          <TableHead
                            key={`race-${position}`}
                            className="text-center cursor-pointer bg-white sticky top-0 hover:bg-gray-50 transition-colors"
                            onClick={() =>
                              sortDataByPosition("raceTime", position)
                            }
                          >
                            <div className="flex items-center justify-center">
                              T{position + 4}
                              <SortArrow
                                isActive={
                                  sortConfig.key === `raceTime_p${position}`
                                }
                                direction={sortConfig.direction}
                              />
                            </div>
                          </TableHead>
                        ))}
                        <TableHead
                          className="text-center cursor-pointer bg-white sticky top-0 hover:bg-gray-50 transition-colors"
                          onClick={() => sortData("totalROI")}
                        >
                          <div className="flex items-center justify-center">
                            Sum&nbsp;of ROI&apos;s
                            <SortArrow
                              isActive={sortConfig.key === "totalROI"}
                              direction={sortConfig.direction}
                            />
                          </div>
                        </TableHead>
                        <TableHead
                          className="text-center cursor-pointer bg-white sticky top-0 hover:bg-gray-50 transition-colors"
                          onClick={() => sortData("totalWinRate")}
                        >
                          <div className="flex items-center justify-center">
                            Overall W/R
                            <SortArrow
                              isActive={sortConfig.key === "totalWinRate"}
                              direction={sortConfig.direction}
                            />
                          </div>
                        </TableHead>
                        <TableHead className="text-center bg-white sticky top-0">
                          Action
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTracks.map((track, index) => {
                        const isExpanded = expandedRows.has(track.name);
                        return (
                          <React.Fragment key={track.name}>
                            <TableRow
                              className="cursor-pointer hover:bg-gray-50"
                              onClick={() => {
                                setExpandedRows((prev) => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(track.name)) {
                                    newSet.delete(track.name);
                                  } else {
                                    newSet.add(track.name);
                                  }
                                  return newSet;
                                });
                              }}
                            >
                              <TableCell className="text-gray-500">
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span>{track.name}</span>
                                  <span className="text-gray-400">
                                    {isExpanded ? "▼" : "▶"}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                {track.totalRaces.toLocaleString()}
                              </TableCell>
                              {/* Early Tips T1-T4 */}
                              {[1, 2, 3, 4].map((position) => (
                                <TableCell
                                  key={`early-${position}`}
                                  className="p-0"
                                >
                                  <RoiCell
                                    data={
                                      track.early?.positions?.[`p${position}`]
                                    }
                                    trackName={track.name}
                                    tipLine={`T${position} (Early Morning)`}
                                    displayMode={roiDisplayMode}
                                    minWinRate={track.minWinRate}
                                    maxWinRate={track.maxWinRate}
                                  />
                                </TableCell>
                              ))}
                              {/* Race Time Tips T5-T8 */}
                              {[1, 2, 3, 4].map((position) => (
                                <TableCell
                                  key={`race-${position}`}
                                  className="p-0"
                                >
                                  <RoiCell
                                    data={
                                      track.raceTime?.positions?.[
                                        `p${position}`
                                      ]
                                    }
                                    trackName={track.name}
                                    tipLine={`T${position + 4} (Race Time)`}
                                    displayMode={roiDisplayMode}
                                    minWinRate={track.minWinRate}
                                    maxWinRate={track.maxWinRate}
                                  />
                                </TableCell>
                              ))}
                              {/* Total ROI */}
                              <TableCell className="text-center font-medium">
                                <span
                                  className={`px-2 py-1 rounded text-sm ${
                                    track.totalROI > 0
                                      ? "bg-green-100 text-green-800"
                                      : track.totalROI < 0
                                        ? "bg-red-100 text-red-800"
                                        : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {track.totalROI?.toFixed(1)}%
                                </span>
                              </TableCell>
                              {/* Total Win Rate */}
                              <TableCell className="text-center font-medium">
                                <span
                                  className={`px-2 py-1 rounded text-sm ${getRelativeColor(track.totalWinRate || 0, totalWinRateStats.min, totalWinRateStats.max)}`}
                                >
                                  {track.totalWinRate?.toFixed(1)}%
                                </span>
                                {/* Debug info */}
                                {/* <div className="text-xs text-gray-500">
                            Value: {track.totalWinRate?.toFixed(1)}%, Min: {totalWinRateStats.min?.toFixed(1)}%, Max: {totalWinRateStats.max?.toFixed(1)}%
                          </div> */}
                              </TableCell>
                              <TableCell>
                                <Link
                                  to={`/analysis/${track.state}/${track.name.toLowerCase().replace(/\s+/g, "-")}`}
                                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 mr-3 px-4 md:px-3 py-3 md:py-2 border-[#F30099] text-[#F30099] hover:bg-[#F30099] hover:text-white transition-all duration-200 w-fit rounded-lg shadow-sm hover:shadow-md border"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Search className="w-4 h-4" /> View All
                                </Link>
                              </TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={14} className="py-2 px-4">
                                  <div className="flex gap-8 text-sm items-center">
                                    <Link
                                      to={`/analysis/${track.state}/${track.name.toLowerCase().replace(/\s+/g, "-")}`}
                                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 px-4 md:px-3 py-3 md:py-2 border-[#F30099] text-[#F30099] hover:bg-[#F30099] hover:text-white transition-all duration-200 w-fit rounded-lg shadow-sm hover:shadow-md border"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Search className="w-4 h-4" />
                                      View All {track.name} Races
                                    </Link>
                                    <div>State: {track.stateName}</div>
                                    <div>
                                      Total Wins: {track.wins.toLocaleString()}
                                    </div>
                                    <div>
                                      Total Tips:{" "}
                                      {track.totalBets.toLocaleString()}
                                    </div>
                                    <div
                                      className={
                                        track.netReturns >= 0
                                          ? "text-green-600 font-bold"
                                          : "text-red-600 font-bold"
                                      }
                                    >
                                      Net Returns:{" "}
                                      {track.netReturns >= 0 ? "+" : "-"}$
                                      {Math.abs(
                                        track.netReturns,
                                      ).toLocaleString()}
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="relative">
                {/* Blurred Skeleton */}
                <div className="filter blur-sm pointer-events-none">
                  <div className="bg-gray-50 rounded-lg p-6 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="space-y-2">
                          <div className="h-5 bg-gray-200 rounded w-24"></div>
                          <div className="h-10 bg-gray-200 rounded w-full"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="overflow-x-auto overflow-y-auto border rounded-lg">
                    <div className="p-4 space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex space-x-4">
                          <div className="w-12 h-8 bg-gray-200 rounded"></div>
                          <div className="flex-1 h-8 bg-gray-200 rounded"></div>
                          <div className="w-24 h-8 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* CTA Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center bg-white/95 p-6 rounded-lg shadow-lg border border-[#F30099]/20 max-w-md">
                    <div className="mb-4 flex justify-center">
                      <div className="w-12 h-12 rounded-full bg-[#F30099]/10 flex items-center justify-center">
                        <LockIcon className="w-6 h-6 text-[#F30099]" />
                      </div>
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Get Access to Track Performance Data
                    </h4>
                    <p className="text-gray-600 mb-4">
                      Sign up now to unlock complete access to detailed track
                      performance analysis and historical data.
                    </p>
                    <button
                      onClick={() => setIsAuthModalOpen(true)}
                      className="inline-block bg-[#F30099] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#F30099]/90 transition-colors"
                    >
                      Get Tips Now
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
